import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { ButtonReset } from '@helpers/global';
import { Device, from } from '@helpers/media';
import { slickCss, slickThemeCss } from '@helpers/slick';
import { verticalFadeIn } from '@helpers/variants';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Container = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: verticalFadeIn(0),
})<{ hideBackdrop?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: 24px 8px;
  z-index: 10000;
  display: grid;
  place-items: center;

  ${({ hideBackdrop }) =>
    !hideBackdrop &&
    css`
      background-color: rgba(0 0 0 / 70%);
      backdrop-filter: blur(2px);
    `}

  @media ${from(Device.MobileLarge)} {
    padding: 32px 24px;
  }

  @media ${from(Device.Tablet)} {
    padding: 32px 48px;
  }
`;

const Content = styled(m.div).attrs({ variants: verticalFadeIn(30) })`
  background-color: ${brand.white};
  width: 100%;
  max-height: 100%;
  position: relative;
  padding-top: 32px;
  border-radius: 4px 0px 4px 4px;
  max-width: 800px;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    max-width: 1200px;
    padding: 0px;
  }
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  ${ButtonReset};
  cursor: pointer;
  position: absolute;
  z-index: 3;
  top: 0px;
  right: 0px;
  width: 32px;
  height: 32px;
  padding: 8px;
  background-color: ${brand.black};
  color: ${brand.white};

  display: grid;
  place-items: center;

  @media ${from(Device.Tablet)} {
    width: 48px;
    height: 48px;
    padding: 14px;

    transition: transform 0.3s ${animation.easeInOutCubic};
    transform-origin: top right;

    svg {
      transition: transform 0.3s ${animation.easeInOutCubic};
    }

    &:hover {
      svg {
        transform: scale(1.2);
      }
    }
    &:active {
      transform: scale(0.9);
    }
  }
`;

const MediaWrapper = styled.div`
  width: 100%;
  max-height: 75vh;
  overflow: hidden;
  position: relative;

  .wrapper {
    width: 100%;
    overflow: visible;
  }

  .content {
    display: grid;
    width: 100%;
  }
`;

const ImageWrapper = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: verticalFadeIn(0),
})`
  margin: 0 auto;
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  max-width: 65vh;
  aspect-ratio: 1;
  position: relative;

  @media ${from(Device.TabletLarge)} {
    max-width: 70vh;
  }
`;

const Image = styled.img.attrs({
  draggable: 'false',
})`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  inset: 0;
`;

const CarouselControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 8px;

  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 3;

  @media ${from(Device.Tablet)} {
    padding: 0 16px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 12px;
  background-color: ${brand.grey[95]};
  width: 100%;
  border-top: 4px solid ${brand.red.base};
  border-radius: 0 0 4px 4px;

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-auto-flow: dense;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media ${from(Device.TabletLarge)} {
    gap: 16px;
    justify-content: flex-end;
    grid-column: 2;
  }
`;

const BaseControlButton = styled.button.attrs({ type: 'button' })`
  ${ButtonReset};

  width: 40px;
  height: 40px;
  background-color: ${brand.white};
  box-shadow: 0px 0px 9px 2px rgba(0 0 0 / 10%);
  border-radius: 50%;
  pointer-events: all;

  display: grid;
  place-items: center;
  padding: 10px;
  transition: box-shadow 0.3s ${animation.easeInOutCubic}, transform 0.3s ${animation.easeInOutCubic};

  svg {
    width: 100%;
    color: ${brand.black};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;

    svg {
      color: ${brand.grey[50]};
    }
  }

  &:hover {
    box-shadow: 2px 2px 9px 4px rgba(0 0 0 / 16%);
    transform: translateY(-2px);
  }

  @media ${from(Device.TabletLarge)} {
    width: 54px;
    height: 54px;
    padding: 15px;
  }
`;

const CarouselControlButton = styled(BaseControlButton)<{ direction: 'prev' | 'next' }>`
  ${({ direction }) =>
    direction === 'prev' &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}

  padding: 15px;

  @media ${from(Device.TabletLarge)} {
    padding: 21px;
  }
`;

const FitToScreenControlButton = styled(BaseControlButton)``;

const ZoomControlButton = styled(BaseControlButton)`
  padding: 8px;

  @media ${from(Device.TabletLarge)} {
    padding: 12px;
  }
`;

const NavItem = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  aspect-ratio: 1;
  background-color: ${brand.white};
  position: relative;
  height: 100%;

  margin: 0 8px;
  border: 3px solid transparent;
  border-radius: 4px;
  transition: border-color 0.3s ${animation.easeInOutCubic}, transform 0.3s ${animation.easeInOutCubic},
    opacity 0.3s ${animation.easeInOutCubic};
  transform: scale(0.75);
  opacity: 0.5;

  img {
    position: absolute;
    inset: 0;
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;

const Nav = styled.div`
  width: 100%;
  overflow: hidden;
  grid-column: 1;
  ${slickCss};
  ${slickThemeCss};

  .slick-slider {
    padding: 0 8px;
  }

  .slick-list {
    overflow: visible;
  }

  .slick-slide {
    @media ${from(Device.TabletLarge)} {
      max-width: 20%;
    }

    &.slick-current ${NavItem}, &:hover ${NavItem} {
      border-color: ${brand.red.base};
      transform: scale(0.9);
      opacity: 1;
    }
  }

  @media ${from(Device.TabletLarge)} {
    .slick-slide {
      transform: scale(1);
    }
  }
`;

const ImageZoomModalStyles = {
  Container,
  Content,
  CloseButton,
  MediaWrapper,
  ImageWrapper,
  Image,
  CarouselControls,
  CarouselControlButton,
  FitToScreenControlButton,
  Footer,
  ZoomControls,
  ZoomControlButton,
  Nav,
  NavItem,
};

export default ImageZoomModalStyles;
